/**
 * Suite Shell Adapter — app-local wiring for @levelup/ui-shell.
 *
 * ARCHITECTURE: This file is app-local and MUST NOT be moved to a shared package.
 * It maps this app's Redux state, hooks, routes, and icons to the shell's adapter contract.
 *
 * ADAPTER BRIDGES (documented gaps):
 * - selectors.tokenWorkspaceId: bridged from boot context currentWorkspace.id.
 *   This app does not decode the JWT (by design: "Token-only management. No JWT decode.").
 *   The bridge is safe because BootGate ensures boot context is populated before shell renders,
 *   and workspace switch updates currentWorkspace.id synchronously.
 *
 * - SwitchContextWorkspace.slug: defaults to '' if Central omits it.
 *   Central may not send slug for all workspaces yet.
 *
 * - SwitchContextWorkspace.type: defaults to 'business' if Central omits it.
 *   Central may not send type for all workspaces yet.
 *
 * - SwitchContextWorkspace.isPrimary: defaults to false if Central omits it.
 *   Central may not send isPrimary for all workspaces yet.
 *
 * - hooks.useGetMenuQuery: wraps static local menu (no server-side menu endpoint).
 *   See useGetMenuQuery.ts for details.
 */

import { ReactNode } from 'react';
import {
  LayoutDashboard,
  Sparkles,
  Lightbulb,
  ListTodo,
  Bell,
  MessageSquare,
  Settings,
} from 'lucide-react';
import type {
  SuiteShellAdapter,
  SuiteApp,
  SwitchContextMeta,
  SwitchContextWorkspace,
  SwitchContextUser,
} from '@levelup/ui-shell';
import { resolveDisplayName } from '@levelup/central-auth';
import type { RootState } from '../store';
import type { BootWorkspace } from '../store/slices/bootSlice';
import { useAuth } from '../hooks/useAuth';
import { useGetMenuQuery } from '../hooks/useGetMenuQuery';
import { useUnreadCount } from '../hooks/useUnreadCount';
import { useTestingModeState } from '../hooks/useTestingModeState';
import { NotificationPanel } from '../components/NotificationPanel';
import { TestingModePanelAdapter } from '../components/TestingModePanelAdapter';

// ---------------------------------------------------------------------------
// Selector helpers
// ---------------------------------------------------------------------------

/**
 * Maps BootWorkspace → SwitchContextWorkspace with safe defaults for bridged fields.
 */
function mapWorkspace(ws: BootWorkspace): SwitchContextWorkspace {
  return {
    id: ws.id,
    name: ws.name,
    role: ws.role,
    slug: ws.slug ?? '',
    type: ws.type ?? 'business',
    isPrimary: ws.isPrimary ?? false,
    status: ws.status,
    capabilities: ws.capabilities,
    permissions: ws.permissions as SwitchContextWorkspace['permissions'],
  };
}

// ---------------------------------------------------------------------------
// Module-level selectors (MUST be stable references — no inline closures)
// ---------------------------------------------------------------------------

const selectApps = (state: RootState): SuiteApp[] =>
  (state.boot.context?.apps ?? []).map((app) => ({
    appId: app.appId,
    name: app.displayName,
    webUrl: app.webUrl,
    iconUrl: app.iconUrl,
    status: app.status as SuiteApp['status'],
    order: app.order,
    mobileScheme: app.mobileScheme,
    // Extended fields enable shell v1.1 icon/theme resolution (hasExtendedFields → true)
    displayName: app.displayName,
    ...(app.assets ? { assets: app.assets } : {}),
    ...(app.ui ? { ui: app.ui } : {}),
  }));

const selectMeta = (state: RootState): SwitchContextMeta | null =>
  (state.boot.context?.meta as SwitchContextMeta | undefined) ?? null;

const selectCurrentWorkspace = (state: RootState): SwitchContextWorkspace | null => {
  const ws = state.boot.context?.currentWorkspace;
  return ws ? mapWorkspace(ws) : null;
};

const selectUser = (state: RootState): SwitchContextUser | null => {
  const u = state.boot.context?.user;
  if (!u) return null;
  // Canonicalize display name via shared identity helper (email is fallback only).
  const fullName =
    u.fullName ?? u.name ?? resolveDisplayName({ fullName: null, email: u.email });
  return {
    id: u.id,
    email: u.email,
    fullName,
    avatarUrl: u.avatarUrl ?? null,
    platformInternal: u.platformInternal,
    platformSuperAdmin: u.platformSuperAdmin,
  } as SwitchContextUser;
};

const selectOtherWorkspaces = (state: RootState): SwitchContextWorkspace[] =>
  (state.boot.context?.otherWorkspaces ?? []).map(mapWorkspace);

/**
 * BRIDGE: tokenWorkspaceId from boot context (no JWT decode in this app).
 * Safe because BootGate populates context before shell renders.
 */
const selectTokenWorkspaceId = (state: RootState): string | null =>
  state.boot.context?.currentWorkspace?.id ?? null;

const selectCreateWorkspaceUrl = (state: RootState): string | null =>
  state.boot.context?.meta?.uiHints?.createWorkspaceUrl ?? null;

// ---------------------------------------------------------------------------
// Menu routes and icons (module-level constants)
// ---------------------------------------------------------------------------

const MENU_ROUTES: Record<string, string> = {
  dashboard: '/dashboard',
  recommendations: '/recommendations',
  plans: '/plans',
  alerts: '/alerts',
  agents: '/agents',
  chat: '/chat',
  settings: '/settings',
};

const MENU_ICONS: Record<string, (className?: string) => ReactNode> = {
  dashboard: (cls) => <LayoutDashboard className={cls} />,
  recommendations: (cls) => <Lightbulb className={cls} />,
  plans: (cls) => <ListTodo className={cls} />,
  alerts: (cls) => <Bell className={cls} />,
  agents: (cls) => <Sparkles className={cls} />,
  chat: (cls) => <MessageSquare className={cls} />,
  settings: (cls) => <Settings className={cls} />,
};

function getMenuIcon(menuKey: string, className?: string): ReactNode {
  const iconFn = MENU_ICONS[menuKey];
  return iconFn ? iconFn(className) : null;
}

// ---------------------------------------------------------------------------
// Assembled adapter (module-level constant — never inline)
// ---------------------------------------------------------------------------

export const SHELL_ADAPTER: SuiteShellAdapter<RootState> = {
  selectors: {
    apps: selectApps,
    meta: selectMeta,
    currentWorkspace: selectCurrentWorkspace,
    user: selectUser,
    otherWorkspaces: selectOtherWorkspaces,
    tokenWorkspaceId: selectTokenWorkspaceId,
    createWorkspaceUrl: selectCreateWorkspaceUrl,
  },
  hooks: {
    useAuth,
    useGetMenuQuery,
    useUnreadCount,
    useTestingModeState,
  },
  config: {
    appId: 'levelup-legacy',
    offloadRoute: '/offload-responsibilities',
    settingsRoute: '/settings',
  },
  adapters: {
    menuRoutes: MENU_ROUTES,
    getMenuIcon,
    NotificationPanel,
    TestingModePanel: TestingModePanelAdapter,
  },
};
